import {
  ChangeDetectionStrategy,
  inject,
  signal,
  type SimpleChanges,
  type TemplateRef,
  input,
} from '@angular/core';
import { Component } from '@angular/core';

import { BehaviorSubject, Observable, Subject, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { NoteId } from '@core/notes-types';
import type { Reactions } from '@core/reactions-types';

import type { ReactionData } from '../../types/reaction-data.types';
import { ReactionsComponent } from '../../ui/reactions/reactions.component';

export abstract class NotesReactionsService {
  public abstract createReaction(noteId: NoteId, unicode: string): void;
  public abstract deleteReaction(noteId: NoteId, unicode: string): void;
  public abstract flattenReactions(reactions: Reactions): Observable<ReactionData[]>;
  public abstract getEmojiShortName(unicode: string): string;
}

@Component({
  selector: 'rt-note-reactions-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: ` <rt-reactions
    [reactionsData]="reactionsData()"
    [projectedContentTpl]="projectedContentTpl()"
    (addExistingReaction)="addExistingReaction($event)"
    (addNewReaction)="addNewReaction($event)"
    (removeReaction)="removeReaction($event)"
  />`,
  imports: [ReactionsComponent],
})
export class NoteReactionsContainerComponent {
  private readonly notesReactionsService = inject(NotesReactionsService);

  public readonly reactions = input<Reactions>(undefined!);
  public readonly noteId = input<NoteId>(undefined!);
  public readonly projectedContentTpl = input<TemplateRef<any>>(undefined!);

  public reactionsData = signal<ReactionData[]>([]);

  private reactions$ = new BehaviorSubject<Reactions>([]);
  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reactions?.currentValue) {
      this.reactions$.next(this.reactions());
    }
  }

  public ngOnInit(): void {
    this.reactions$
      .pipe(
        switchMap(reactions => this.notesReactionsService.flattenReactions(reactions)),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(reactionsData => this.reactionsData.set(reactionsData));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public addNewReaction(unicode: string): void {
    this.reactionsData.update(v => [
      ...v,
      {
        currentUserReacted: true,
        count: 1,
        reaction: unicode,
        otherUsersWhoReacted: [],
        reactionShortName: this.notesReactionsService.getEmojiShortName(unicode),
      },
    ]);
    this.notesReactionsService.createReaction(this.noteId(), unicode);
  }

  public addExistingReaction(unicode: string): void {
    this.reactionsData.update(v =>
      v.reduce(
        (acc, curr) => {
          if (curr.reaction !== unicode) {
            return [...acc, curr];
          } else {
            return [
              ...acc,
              {
                ...curr,
                count: curr.count + 1,
                currentUserReacted: true,
              },
            ];
          }
        },
        <ReactionData[]>[],
      ),
    );
    this.notesReactionsService.createReaction(this.noteId(), unicode);
  }

  public removeReaction(unicode: string): void {
    this.reactionsData.update(v =>
      v.reduce(
        (acc, curr) => {
          if (curr.reaction !== unicode) {
            return [...acc, curr];
          } else {
            if (curr.count === 1) {
              return acc;
            } else {
              return [
                ...acc,
                {
                  ...curr,
                  count: curr.count - 1,
                  currentUserReacted: false,
                },
              ];
            }
          }
        },
        <ReactionData[]>[],
      ),
    );
    this.notesReactionsService.deleteReaction(this.noteId(), unicode);
  }
}
